import React, { useState } from 'react';
import { EXPERIENCE, OTHER_EXPERIENCE } from '../data';
import { useIsMobile } from '../hooks/useIsMobile';

function RoleBlock({ role, period, bullets, isNested }) {
  return (
    <div style={{
      paddingLeft: isNested ? '24px' : '0',
      borderLeft: isNested ? '1px solid rgba(26,26,26,0.1)' : 'none',
      marginBottom: '28px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.01em' }}>{role}</div>
        </div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.1em', color: 'var(--ink-light)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{period}</div>
      </div>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {bullets.map((b, i) => (
          <li key={i} style={{
            fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 300,
            color: 'var(--ink-mid)', lineHeight: 1.75, marginBottom: '6px',
            paddingLeft: '18px', position: 'relative',
          }}>
            <span style={{ position: 'absolute', left: 0, color: 'var(--accent)', fontWeight: 700 }}>—</span>
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Experience() {
  const isMobile = useIsMobile();
  const [openIdx, setOpenIdx] = useState(0);
  const [showOther, setShowOther] = useState(false);

  return (
    <section id="experience" style={{ padding: '80px 0', borderTop: '1px solid rgba(26,26,26,0.06)' }}>
      <div style={{ padding: '0 12%' }}>
        <div className="fade-in" style={{ marginBottom: '56px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.3em', color: 'var(--ink-light)', textTransform: 'uppercase', marginBottom: '16px' }}>
            Professional Experience
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 600, color: 'var(--ink)', lineHeight: 1, letterSpacing: '-0.03em' }}>
            Where I've Built
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '280px 1fr', gap: '0' }}>
          {/* Left — company list */}
          <div style={{
            borderRight: isMobile ? 'none' : '1px solid rgba(26,26,26,0.08)',
            borderBottom: isMobile ? '1px solid rgba(26,26,26,0.08)' : 'none',
            paddingRight: isMobile ? '0' : '40px',
            display: isMobile ? 'flex' : 'block',
            overflowX: isMobile ? 'auto' : 'visible',
            marginBottom: isMobile ? '0' : '0',
          }}>
            {EXPERIENCE.map((exp, i) => (
              <div key={i}
                onClick={() => setOpenIdx(i)}
                style={{
                  padding: isMobile ? '12px 16px' : '20px 0',
                  borderBottom: isMobile ? 'none' : '1px solid rgba(26,26,26,0.06)',
                  borderRight: isMobile ? '1px solid rgba(26,26,26,0.06)' : 'none',
                  cursor: 'crosshair', transition: 'all 0.2s',
                  whiteSpace: isMobile ? 'nowrap' : 'normal',
                  flexShrink: isMobile ? 0 : undefined,
                }}
              >
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 600,
                  color: openIdx === i ? 'var(--ink)' : 'var(--ink-light)',
                  letterSpacing: '-0.01em', marginBottom: '4px',
                  transition: 'color 0.2s',
                }}>
                  {exp.company}
                </div>
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.12em',
                  color: openIdx === i ? 'var(--accent)' : 'transparent',
                  textTransform: 'uppercase', transition: 'color 0.2s',
                }}>
                  {exp.role || 'Multiple Roles'}
                </div>
                {openIdx === i && (
                  <div style={{ width: '20px', height: '2px', background: 'var(--ink)', marginTop: '10px' }} />
                )}
              </div>
            ))}
          </div>

          {/* Right — detail */}
          <div style={{ paddingLeft: isMobile ? '0' : '60px', paddingTop: isMobile ? '24px' : '8px' }}>
            {(() => {
              const exp = EXPERIENCE[openIdx];
              if (!exp) return null;
              if (exp.roles) {
                return (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                      <div>
                        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.02em', marginBottom: '4px' }}>{exp.company}</h3>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.15em', color: 'var(--ink-light)', textTransform: 'uppercase' }}>{exp.location} · {exp.roles.length} Roles · Career Progression</div>
                      </div>
                    </div>
                    {exp.roles.map((r, i) => (
                      <RoleBlock key={i} {...r} isNested={true} />
                    ))}
                  </div>
                );
              }
              return (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.02em', marginBottom: '4px' }}>{exp.company}</h3>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontStyle: 'italic', color: 'var(--ink-mid)' }}>{exp.role}</div>
                        {exp.tag === 'startup' && (
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.15em', color: 'var(--accent)', border: '1px solid var(--accent)', padding: '2px 8px', textTransform: 'uppercase' }}>Startup</span>
                        )}
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.12em', color: 'var(--ink-light)', textTransform: 'uppercase', marginTop: '4px' }}>{exp.location}</div>
                    </div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.1em', color: 'var(--ink-light)', textTransform: 'uppercase' }}>{exp.period}</div>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {exp.bullets.map((b, i) => (
                      <li key={i} style={{
                        fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 300,
                        color: 'var(--ink-mid)', lineHeight: 1.8, marginBottom: '10px',
                        paddingLeft: '18px', position: 'relative',
                      }}>
                        <span style={{ position: 'absolute', left: 0, color: 'var(--accent)', fontWeight: 700 }}>—</span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Other Experience sub-section */}
        <div style={{ marginTop: '64px', borderTop: '1px solid rgba(26,26,26,0.06)', paddingTop: '48px' }}>
          <button
            onClick={() => setShowOther(!showOther)}
            style={{
              fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase',
              color: 'var(--ink-mid)', background: 'none', border: '1px solid rgba(26,26,26,0.15)',
              padding: '10px 20px', cursor: 'crosshair', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: '10px',
            }}
            onMouseEnter={e => { e.target.style.background = 'var(--ink)'; e.target.style.color = 'var(--bg)'; }}
            onMouseLeave={e => { e.target.style.background = 'none'; e.target.style.color = 'var(--ink-mid)'; }}
          >
            {showOther ? '↑ Hide' : '↓ Show'} Other Experience
          </button>

          {showOther && (
            <div style={{ marginTop: '32px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: '24px' }}>
              {OTHER_EXPERIENCE.map((exp, i) => (
                <div key={i} style={{ borderLeft: '2px solid rgba(26,26,26,0.08)', paddingLeft: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.01em' }}>{exp.role}</div>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '8px', letterSpacing: '0.15em', color: 'var(--ink-light)', border: '1px solid rgba(26,26,26,0.12)', padding: '2px 8px', textTransform: 'uppercase' }}>{exp.tag}</span>
                  </div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--ink-mid)', fontWeight: 300, marginBottom: '4px' }}>{exp.company}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.1em', color: 'var(--ink-light)', textTransform: 'uppercase', marginBottom: '12px' }}>
                    {exp.period} · {exp.location}
                  </div>
                  {exp.bullets.map((b, j) => (
                    <div key={j} style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--ink-mid)', fontWeight: 300, lineHeight: 1.7 }}>{b}</div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
